import Bugsnag from "@bugsnag/js";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components";

import { addToast } from "../../Store/Actions";
import axiosInstance from "../../axiosInstance";
import { trackEvent } from "../../helperFunctions";
import CheckGreen from "../../assets/check-green.svg";
import URL from "../../urls";
import FlexBox from "../common/FlexBox";
import { Button } from "../common/Buttons";
import { H3, H4 } from "../common/Headings";
import Loader from "../common/Loader";
import { Modal } from "../common/Modal";

const Content = styled(FlexBox)`
  min-height: 20rem;
  align-items: center;
  flex: 1;
  padding: 1.5rem;
  @media screen and (max-width: 768px) {
    padding: 1rem;
    min-height: auto;
  }
`;

const Footer = styled(FlexBox)`
  padding: 1rem 1.5rem;
  @media screen and (max-width: 768px) {
    padding: 0 1rem 1rem;
  }
`;

const CheckIcon = styled.img`
  height: 6rem;
  aspect-ratio: 1;
`;

const metadata = {
  dropdown: {
    info: {
      title: "Are you sure about sending this client a follow-up email?",
      subtitle:
        "Please ensure that you have sent this client a follow-up message on chat using the /followup template. Clicking on “SEND” will send the client an email notifying them of new messages in their chat.",
    },
    success:
      "We have sent a follow-up email about your message to this client. You will not be able to send another follow-up email for the next 7 days.",
  },
  shortcut: {
    info: {
      title: "Do you also want to send this client a follow-up email?",
      subtitle:
        "Clicking on “SEND” will send this client a follow-up email notifying them of new messages in their chat. This will remind them to check their chat box for your follow-up message.",
    },
    success:
      "A follow-up email about your message has been sent to this client. You will not be able to send another follow-up email for the next 7 days.",
  },
};

const FollowupModal = ({
  chatClient,
  source = "dropdown",
  toggleModal = () => {},
}) => {
  const dispatch = useDispatch();
  const user = useSelector(state => state?.auth?.user);

  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  let clientProfile = chatClient;

  const analyticPayload = {
    client_id: clientProfile?.client?.info?.id,
    [`${user?.usertype}_UUID`]: user?.uuid,
    [`${user?.usertype}_name`]: user?.name,
  };

  useEffect(() => {
    let interval;

    if (success) {
      interval = setTimeout(() => {
        toggleModal();
      }, 3000);
    }

    return () => clearTimeout(interval);
  }, [success]);

  const sendFollowupEmail = async () => {
    const clientUuid = clientProfile?.client?.info?.uuid;
    if (!clientUuid) return;

    try {
      setLoading(true);

      await axiosInstance.get(
        `${URL.clientList}/${clientUuid}/send_followup_email`
      );

      setSuccess(true);
      trackEvent({
        event: "resurrect_email_reminder_sent",
        payload: analyticPayload,
      });
    } catch (error) {
      dispatch(
        addToast({
          msg: error?.response?.data?.message,
          appearance: "error",
          autoDismiss: true,
        })
      );
      Bugsnag.notify(error);
      toggleModal();
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      XS
      width="27rem"
      height="auto"
      borderRadius="1rem"
      mobileHeight="auto"
      mobileWidth="90%"
      mobileBorderRadius="1rem"
    >
      {loading ? (
        <Content>
          <Loader />
        </Content>
      ) : success ? (
        <FlexBox column padding="2.5rem 1.5rem">
          <CheckIcon
            draggable={false}
            src={CheckGreen}
            alt="Followup Email Sent"
          />
          <FlexBox column align="center" rowGap="0.5rem" margin="1.5rem 0 0">
            <H3 bold margin="0 0 1rem 0">
              Follow up e-mail sent successfully.
            </H3>
            <H4 textAlign="center">{metadata?.[source]?.success}</H4>
          </FlexBox>
        </FlexBox>
      ) : (
        <>
          <Content column>
            <H3 bold margin="0 0 1rem 0">
              {metadata?.[source]?.info?.title}
            </H3>
            <H4>{metadata?.[source]?.info?.subtitle}</H4>
          </Content>
          <Footer justify="space-between">
            <Button outline secondary onClick={toggleModal}>
              CANCEL
            </Button>
            <Button onClick={sendFollowupEmail}>SEND</Button>
          </Footer>
        </>
      )}
    </Modal>
  );
};

export default FollowupModal;
